import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';

const OTPModal = ({ open, onClose, onSubmit, onGenerateOTP, action, isLoading }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    if (value && index < 5) {
      requestAnimationFrame(() => {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');

    if (/^\d{6}$/.test(pastedData)) {
      const otpArray = pastedData.split('');
      setOtp(otpArray);
      setError('');

      setTimeout(() => {
        const lastInput = inputRefs.current[5];
        if (lastInput) {
          lastInput.focus();
        }
      }, 100);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (e.key === 'Enter' && otp.join('').length === 6) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }
    setError('');
    onSubmit(otpString);
  };

  const handleClose = () => {
    setOtp(['', '', '', '', '', '']);
    setError('');
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setOtp(['', '', '', '', '', '']);
      setError('');
    } else {
      setTimeout(() => {
        const firstInput = inputRefs.current[0];
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    }
  }, [open]);

  const handleGenerateOTP = () => {
    setError('');
    onGenerateOTP();
  };

  const getTitle = () => {
    switch (action) {
      case 'view_secret':
        return 'Enter OTP';
      case 'regenerate_secret':
        return 'Generate a New Client Secret';
      default:
        return 'OTP Verification';
    }
  };

  const getDescription = () => {
    switch (action) {
      case 'view_secret':
        return 'To view your secret, please enter the 6-digit OTP sent to your email address.';
      case 'regenerate_secret':
        return null;
      default:
        return 'Please enter the OTP sent to your registered email.';
    }
  };

  const getWarningText = () => {
    if (action === 'regenerate_secret') {
      return (
        <>
          <strong>Warning:</strong> Your current secret will be invalidated immediately. Any active integrations will
          stop working until you update them with the new secret.
        </>
      );
    }
    return null;
  };

  const getButtonText = () => {
    switch (action) {
      case 'view_secret':
        return 'View Secret';
      case 'regenerate_secret':
        return 'Confirm & Regenerate';
      default:
        return 'Submit';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: '435px',
          maxWidth: '90vw',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        {getTitle()}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {getDescription()}
        </Typography>

        {getWarningText() && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              '& .MuiAlert-icon': {
                alignItems: 'flex-start',
                mt: 0.5,
              },
            }}
            icon={
              <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                ⚠️
              </Typography>
            }
          >
            {getWarningText()}
          </Alert>
        )}

        {action === 'regenerate_secret' && (
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
            Enter the 6-digit OTP sent to your email to confirm:
          </Typography>
        )}

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                variant="outlined"
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    width: '32px',
                    height: '32px',
                  },
                }}
                disabled={isLoading}
                error={!!error}
                sx={{
                  width: '56px',
                  margin: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: index === 0 ? '4px 0 0 4px' : index === 5 ? '0 4px 4px 0' : 0,
                    '& fieldset': {
                      borderColor: error ? 'error.main' : 'grey.300',
                      borderRightWidth: index === 5 ? 1 : 0,
                    },
                    '&:hover fieldset': {
                      borderColor: error ? 'error.main' : 'grey.400',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: error ? 'error.main' : 'primary.main',
                    },
                  },
                }}
              />
            ))}
          </Box>
          {error && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', alignItems: 'center' }}>
        {action === 'view_secret' ? (
          <>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isLoading || otp.join('').length !== 6}
              sx={{
                width: '100%',
                mb: 2,
                fontWeight: 'bold',
                py: 1.5,
                textTransform: 'none',
              }}
            >
              {isLoading ? 'Verifying...' : getButtonText()}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Didn&#39;t receive the OTP?{' '}
              <Button
                variant="text"
                onClick={handleGenerateOTP}
                disabled={isLoading}
                sx={{
                  p: 0,
                  minWidth: 'auto',
                  fontWeight: 'bold',
                  color: 'primary.main',
                  textTransform: 'none',
                }}
              >
                Resend
              </Button>
            </Typography>
          </>
        ) : (
          <>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="error"
              disabled={isLoading || otp.join('').length !== 6}
              sx={{
                width: '100%',
                mb: 2,
                fontWeight: 'bold',
                py: 1.5,
                backgroundColor: 'error.main',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'error.dark',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(211, 47, 47, 0.12)',
                  color: 'white',
                },
              }}
            >
              {isLoading ? 'Processing...' : getButtonText()}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Didn&#39;t receive the OTP?{' '}
              <Button
                variant="text"
                onClick={handleGenerateOTP}
                disabled={isLoading}
                sx={{
                  p: 0,
                  minWidth: 'auto',
                  fontWeight: 'bold',
                  color: 'primary.main',
                  textTransform: 'none',
                }}
              >
                Resend
              </Button>
            </Typography>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OTPModal;
